"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Address = void 0;
const crypto_1 = require("crypto");
const GENERAL_DELIVERY = "General Delivery";
const BOOLEAN_FLAGS = [GENERAL_DELIVERY, "REDACTED_ADDRESS"];
const ADDRESS_FLAGS = [...BOOLEAN_FLAGS, "EXPRESS_CHECKOUT_ADDRESS"];
const EXPRESS_FLAG_VALUES = ["APPLE", "GOOGLE", "PAYPAL"];
const serialiseFlags = (flags) => {
    return flags.length
        ? flags.map(({ name, value }) => `${name}=${value}`).join("\n")
        : "";
};
const notEmpty = (value) => value !== null && value !== undefined;
const isAddressFlagName = (name) => {
    return ADDRESS_FLAGS.includes(name);
};
const isExpressAddressFlag = (flag) => {
    if (typeof flag.value !== "string" || !EXPRESS_FLAG_VALUES.includes(flag.value))
        return false;
    return flag.name === "EXPRESS_CHECKOUT_ADDRESS";
};
const isBooleanAddressFlag = (flag) => {
    if (typeof flag.value !== "boolean")
        return false;
    return BOOLEAN_FLAGS.includes(flag.name);
};
const parseSerialisedBooleanValue = (input) => {
    try {
        return JSON.parse(input);
    }
    catch (err) {
        return "NotABoolean";
    }
};
const deserialiseFlags = (flagString) => {
    return flagString
        .split("\n")
        .map((flag) => {
        const [name, value] = flag.split("=");
        if (!isAddressFlagName(name))
            return;
        const mappedFlag = { name, value };
        if (isExpressAddressFlag(mappedFlag))
            return mappedFlag;
        const booleanFlag = Object.assign(Object.assign({}, mappedFlag), { value: parseSerialisedBooleanValue(value) });
        if (isBooleanAddressFlag(booleanFlag))
            return booleanFlag;
        return;
    })
        .filter(notEmpty);
};
const toAddressLines = (streetName) => {
    const [lineOne, lineTwo, lineThree] = streetName
        .split("|")
        .map((line) => line.trim())
        .filter(Boolean);
    return { lineOne, lineTwo, lineThree };
};
class Address {
    constructor(address) {
        const { flags = [] } = address, rest = __rest(address, ["flags"]);
        this.address = Object.assign({ id: (0, crypto_1.randomUUID)(), flags }, rest);
    }
    static removeGeneralDelivery(addressLine) {
        if (/General ?Delivery/i.test(addressLine)) {
            return {
                line: addressLine.replace(/General ?Delivery/i, "").trim(),
                hasGD: true,
            };
        }
        return { line: addressLine, hasGD: false };
    }
    static fromCT({ streetName = "", city = "", country: countryCode = "", email = "", firstName = "", id = (0, crypto_1.randomUUID)(), lastName = "", phone = "", postalCode: postCode = "", state, additionalAddressInfo = "", }) {
        const { lineOne, lineTwo, lineThree } = toAddressLines(streetName);
        if (!lineOne || !lineOne.length) {
            throw new Error("Failed to parse address, lineOne is empty");
        }
        return new Address({
            flags: deserialiseFlags(additionalAddressInfo),
            city,
            countryCode,
            email,
            firstName,
            id,
            lastName,
            lineOne,
            lineTwo,
            lineThree,
            phone,
            postCode,
            state,
        });
    }
    static fromAddressInput(address, flags = []) {
        const { line, hasGD } = Address.removeGeneralDelivery(address.lineOne || "");
        return new Address({
            flags: hasGD
                ? [...flags, { name: GENERAL_DELIVERY, value: true }]
                : flags,
            city: address.city || "",
            countryCode: address.countryCode || "",
            email: address.email || "",
            firstName: address.firstName,
            id: address.id,
            lastName: address.lastName,
            lineOne: line,
            lineTwo: address.lineTwo || undefined,
            lineThree: address.lineThree || undefined,
            phone: address.phone,
            postCode: address.postCode || "",
            state: address.state || "",
        });
    }
    get streetName() {
        return [this.address.lineOne, this.address.lineTwo, this.address.lineThree]
            .filter(Boolean)
            .join("|");
    }
    getFlag(flagName) {
        var _a;
        return (_a = this.address.flags.find((flag) => flag.name === flagName)) === null || _a === void 0 ? void 0 : _a.value;
    }
    isRedacted() {
        return !!this.getFlag("REDACTED_ADDRESS");
    }
    isExpressCheckoutAddress(type) {
        if (!type)
            return false;
        const flag = this.address.flags.find(isExpressAddressFlag);
        if (!flag)
            return false;
        return flag.value === type;
    }
    set lineOne(lineOne) {
        const { line, hasGD } = Address.removeGeneralDelivery(lineOne);
        this.address.lineOne = line;
        if (hasGD)
            this.setFlag({ name: GENERAL_DELIVERY, value: true });
    }
    setFlag(flag) {
        if (typeof flag === "string")
            throw new Error("Invalid flag: expected object, got string");
        if (isExpressAddressFlag(flag)) {
            this.address.flags = [
                ...this.address.flags.filter(({ name }) => name !== flag.name),
                { name: flag.name, value: flag.value },
            ];
            return;
        }
        if (isBooleanAddressFlag(flag)) {
            this.address.flags = [
                ...this.address.flags.filter(({ name }) => name !== flag.name),
                { name: flag.name, value: flag.value },
            ];
            return;
        }
        throw new Error(`Invalid flag value`);
    }
    get bpAddress() {
        return this.address;
    }
    get ctAddress() {
        const { id = "", city, countryCode: country, email, firstName, lastName, phone, postCode: postalCode, state, } = this.address;
        return {
            id,
            additionalAddressInfo: serialiseFlags(this.address.flags),
            city,
            country,
            email,
            firstName,
            lastName,
            phone,
            postalCode,
            state,
            streetName: this.streetName,
        };
    }
    get avalaraAddress() {
        const { lineOne, lineTwo, city, state: region, postCode } = this.address;
        const hasGeneralDelivery = this.getFlag(GENERAL_DELIVERY);
        return {
            line1: hasGeneralDelivery ? `${lineOne} ${GENERAL_DELIVERY}` : lineOne,
            line2: lineTwo,
            city,
            region,
            postalCode: postCode,
        };
    }
}
exports.Address = Address;
//# sourceMappingURL=index.js.map